<script setup lang="ts">
import { computed, inject, reactive, Ref, ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { formatDate } from '@/utils/dateUtils';
import { useNotesManagementStore } from '@/store/NotesManagementStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import Fluid from 'primevue/fluid';
import InputText from 'primevue/inputtext';
import TableColumn from 'primevue/column';

const dialogRef = inject('dialogRef') as Ref;
const code = dialogRef?.value.data.qId;
const type = dialogRef?.value.data.type;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const currentLanguage = getCurrentLocale();
const notesManagementStore = useNotesManagementStore();
const minDateRangeValue = ref(new Date());
const isFormAdd = ref(false);

const listNotes = computed(() => notesManagementStore.selectListNotes);

const columns = [
  { field: 'noteNo', header: t('note.note_id') },
  { field: 'startDate', header: t('create_quote.start_date') },
  { field: 'endDate', header: t('create_quote.end_date') },
  { field: 'text', header: t('note.content_placeholder') }
];

const noteForm = reactive({
  startDate: null,
  endDate: null,
  content: ''
});

const addNotes = () => {
  isFormAdd.value = true;
};

const onCancel = () => {
  noteForm.startDate = null;
  noteForm.endDate = null;
  noteForm.content = '';
  isFormAdd.value = false;
};

const checkIsBeforeDate = (startDate, endDate) => {
  const sDate = DateTime.fromJSDate(new Date(startDate));
  const eDate = DateTime.fromJSDate(new Date(endDate));
  return eDate > sDate;
};

const onSave = async () => {
  if (checkIsBeforeDate(noteForm.startDate, noteForm.endDate)) {
    const payload = {
      startDate: formatDate(noteForm.startDate),
      endDate: formatDate(noteForm.endDate),
      text: noteForm.content
    };
    if (code) {
      await notesManagementStore.handleAddNotes(code, payload, type);
    }
    await notesManagementStore.handleGetListNotes(code, type);
    toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    onCancel();
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('note.the_end_date_must_be_after') });
  }
};

const onDelete = async (data) => {
  if (code) {
    await notesManagementStore.handleDeleteNotes(code, data.noteNo, type);
  }
  await notesManagementStore.handleGetListNotes(code, type);
};

const renderData = (field, data) => {
  if (field === 'startDate' || field === 'endDate') {
    return formatDate(data, 'dd-LLL-yyyy', false, currentLanguage);
  } else {
    return data;
  }
};
</script>
<template>
  <DataTable :value="listNotes" class="min-w-50rem list-notes">
    <TableColumn v-for="col of columns" :key="col.field" :field="col.field" :header="col.header">
      <template #body="{ data, field }">
        {{ renderData(field, data[field]) }}
      </template>
    </TableColumn>
    <TableColumn>
      <template #body="{ data }">
        <i class="pi pi-times" @click="onDelete(data)"></i>
      </template>
    </TableColumn>
  </DataTable>
  <div class="mt-5" v-if="!isFormAdd">
    <Button id="addNote" @click="addNotes()">{{ $t('note.add_note') }}</Button>
  </div>
  <div id="addNoteForm" v-if="isFormAdd">
    <div class="mt-5">
      <Fluid>
        <div class="field grid">
          <div class="col">
            <label for="noteStartDate">{{ $t('create_quote.start_date') }}</label>
            <DatePicker id="noteStartDate" v-model="noteForm.startDate" :minDate="minDateRangeValue" dateFormat="dd-M-yy" :placeholder="$t('common.date_format')">
              <template #footer>
                <p class="text-center">
                  <small>{{ $t('common.press_space') }}</small>
                </p>
              </template>
            </DatePicker>
          </div>
          <div class="col">
            <label for="noteEndDate">{{ $t('create_quote.end_date') }}</label>
            <DatePicker id="noteEndDate" v-model="noteForm.endDate" :minDate="new Date(noteForm.startDate)" dateFormat="dd-M-yy" :placeholder="$t('common.date_format')">
              <template #footer>
                <p class="text-center">
                  <small>{{ $t('common.press_space') }}</small>
                </p>
              </template>
            </DatePicker>
          </div>
        </div>
      </Fluid>
    </div>
    <div class="mt-5">
      <Fluid>
        <div class="field grid">
          <div class="col">
            <label for="noteContent">{{ $t('note.content_placeholder') }}</label>
            <InputText id="noteContent" v-model="noteForm.content" />
          </div>
        </div>
      </Fluid>
    </div>
    <div class="flex justify-content-between">
      <div>
        <Button id="cancelNote" @click="onCancel()">{{ $t('common.buttons.cancel') }}</Button>
      </div>
      <div>
        <Button id="saveNote" @click="onSave()" :disabled="!noteForm.startDate || !noteForm.endDate || !noteForm.content">{{ $t('common.buttons.save') }}</Button>
      </div>
    </div>
  </div>
</template>
